import { useState } from "react";
import { formatDate, formatDurationSeconds } from "../format.js";
import { Breadcrumb } from "../shell/Shell.js";
import { useSession } from "../auth/session.js";
import { Card, EmptyState, HonestState, Panel, Tag } from "../components.js";
import { Field, Select } from "../components/controls.js";
import {
  canReadConnectors,
  liveConnectorService,
  type ConnectorCapability,
  type ConnectorService,
  type ConnectorState,
  type ConnectorSyncState,
  type CoverageWindow,
} from "../api/connectorService.js";
import "./connectors.css";

/**
 * E8.11 (NX-2640) — Connectors: where an admin verifies what SeneSight can and cannot see. Design
 * 08 §1/§3 list it as the cross-cutting "source status + coverage" screen; this replaces the
 * ComingSoon stub that stood at /connectors and closes M2's "no ComingSoon stub in primary
 * navigation" exit criterion.
 *
 * Honest degradation is the whole point of the screen, so nothing here fabricates a figure:
 *   - `cannot` dimensions render as "not observable", never as a zero score (connector SDK S4/E3.1)
 *   - `completeness` / `lagSeconds` are optional on the API "only when derivable" — absent renders
 *     as not-derivable, never as 0% or 0s
 *   - a source with no coverage windows renders the "never synced" honest state, not a blank row
 *   - a null sync cursor renders as never-synced rather than as an empty string
 *
 * Every sub-component below is presentational: it takes data through props and never fetches.
 * Only `Connectors` and `ProjectConnectorPanel` touch the injected `ConnectorService` (AC5).
 */

// --- SyncStateBadge: the ingest cursor and how stale it is ------------------
export function SyncStateBadge({ sync }: { sync: ConnectorSyncState }): React.ReactNode {
  if (sync.updatedAt === null && sync.cursorValue === null) {
    return <Tag>Never synced</Tag>;
  }
  return (
    <span className="syncbadge">
      <Tag tone="green">Synced</Tag>
      <span className="meta">
        cursor <code>{sync.cursorValue ?? "—"}</code>
        {" · "}
        {sync.updatedAt === null
          ? "last update not recorded"
          : `updated ${formatDate(sync.updatedAt)}`}
      </span>
    </span>
  );
}

// --- CapabilityManifestPanel: declared vs unobservable ----------------------
export function CapabilityManifestPanel({
  capability,
}: {
  capability: ConnectorCapability;
}): React.ReactNode {
  return (
    <div className="manifest">
      <div className="mrow">
        <span className="mk">Observes</span>
        <span className="mv">
          {capability.provides.length === 0 ? (
            <span className="meta">nothing declared</span>
          ) : (
            capability.provides.map((entity) => (
              <Tag key={entity} tone="green">
                {entity}
              </Tag>
            ))
          )}
        </span>
      </div>
      <div className="mrow">
        <span className="mk">Cannot observe</span>
        <span className="mv">
          {capability.cannot.length === 0 ? (
            <span className="meta">no declared blind spots</span>
          ) : (
            capability.cannot.map((entity) => <Tag key={entity}>{entity} — N/A</Tag>)
          )}
        </span>
      </div>
      <div className="mrow">
        <span className="mk">Delivery</span>
        <span className="mv">
          <Tag tone={capability.webhook ? "green" : undefined}>
            {capability.webhook ? "webhook" : "poll only"}
          </Tag>
          <Tag tone={capability.backfill ? "green" : undefined}>
            {capability.backfill ? "backfill supported" : "no backfill"}
          </Tag>
        </span>
      </div>
    </div>
  );
}

// --- CoverageWindowView: observed windows, or an honest never-synced state --
export function CoverageWindowView({
  coverage,
  sourceType,
}: {
  coverage: readonly CoverageWindow[];
  sourceType: string;
}): React.ReactNode {
  if (coverage.length === 0) {
    return (
      <HonestState
        kind="nodata"
        detail={`No coverage window has been observed for ${sourceType} yet — this source has never synced.`}
        action="Nothing is missing from the data; there is simply nothing to report."
      />
    );
  }
  return (
    <table className="covtable">
      <thead>
        <tr>
          <th scope="col">Entity</th>
          <th scope="col">From</th>
          <th scope="col">Through</th>
          <th scope="col">Completeness</th>
          <th scope="col">Lag</th>
        </tr>
      </thead>
      <tbody>
        {coverage.map((window) => (
          <tr key={`${window.entityType}-${window.from}-${window.through}`}>
            <td>{window.entityType}</td>
            <td>{window.from}</td>
            <td>{window.through}</td>
            {/* "only when derivable" on the API — absent is not-derivable, never 0% */}
            <td>
              {window.completeness === undefined ? (
                <span className="meta">not derivable</span>
              ) : (
                `${Math.round(window.completeness * 100)}%`
              )}
            </td>
            <td>
              {window.lagSeconds === undefined ? (
                <span className="meta">not derivable</span>
              ) : (
                formatDurationSeconds(window.lagSeconds)
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- ConnectorList: one card per declared source ----------------------------
export function ConnectorList({
  connectors,
}: {
  connectors: readonly ConnectorState[];
}): React.ReactNode {
  if (connectors.length === 0) {
    return <EmptyState message="No connectors are declared for this deployment." />;
  }
  return (
    <div className="connlist">
      {connectors.map((connector) => (
        <Card key={connector.sourceType}>
          <div className="connhead">
            <h2>{connector.sourceType}</h2>
            <SyncStateBadge sync={connector.syncState} />
          </div>
          <CapabilityManifestPanel capability={connector.capability} />
          <div className="covwrap">
            <div className="sec">Coverage</div>
            <div className="covscroll">
              <CoverageWindowView coverage={connector.coverage} sourceType={connector.sourceType} />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// --- Per-project panel: mounted only once a project is chosen (AC3) ---------
function ProjectConnectorPanel({
  projectId,
  projectName,
  service,
}: {
  projectId: string;
  projectName: string;
  service: ConnectorService;
}): React.ReactNode {
  const state = service.useProjectConnectors(projectId);
  return (
    <Panel title={`Per-source state for ${projectName}`}>
      {state.status === "loading" && (
        <div className="meta">Loading this project&rsquo;s connector coverage…</div>
      )}
      {state.status === "error" && (
        <EmptyState
          message={`Couldn't load connector state for this project: ${state.error.message}`}
        />
      )}
      {state.status === "ready" && <ConnectorList connectors={state.envelope.data.connectors} />}
    </Panel>
  );
}

// --- The screen ------------------------------------------------------------
export function Connectors({
  service = liveConnectorService,
}: {
  service?: ConnectorService;
} = {}): React.ReactNode {
  const { session } = useSession();
  const forbidden = session ? !canReadConnectors(session.role) : false;
  return (
    <>
      <Breadcrumb items={[{ label: "Portfolio", to: "/portfolio" }, { label: "Connectors" }]} />
      <div className="head">
        <h1>Connectors</h1>
      </div>
      {forbidden ? (
        <EmptyState message="You are signed in, but your role is not authorized to view connector internals." />
      ) : (
        <ConnectorsContent service={service} />
      )}
    </>
  );
}

/**
 * Split from `Connectors` so the service hooks are never called for a role the API will refuse
 * anyway — an unauthorized session makes zero requests, matching how ReviewQueue gates itself.
 */
function ConnectorsContent({ service }: { service: ConnectorService }): React.ReactNode {
  const connectors = service.useConnectors();
  const projects = service.useProjects();
  const [selected, setSelected] = useState<string>("");

  const options = projects.status === "ready" ? projects.envelope.data : [];
  const selectedProject = options.find((project) => project.id === selected);

  return (
    <>
      <div className="meta">
        What each source can observe, what it cannot, and how current it is. Blind spots are shown
        as not-observable rather than scored as zero.
      </div>

      {connectors.status === "loading" && (
        <div className="meta">Loading declared connectors and their sync state…</div>
      )}
      {connectors.status === "error" && (
        <EmptyState message={`Couldn't load connectors: ${connectors.error.message}`} />
      )}
      {connectors.status === "ready" && <ConnectorList connectors={connectors.envelope.data} />}

      <Panel title="Per-project coverage">
        <Field label="Project" className="projpick">
          <Select value={selected} onChange={(event) => setSelected(event.target.value)}>
            <option value="">Select a project…</option>
            {options.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </Field>
        {projects.status === "error" && (
          <EmptyState message={`Couldn't load the project list: ${projects.error.message}`} />
        )}
      </Panel>

      {selectedProject !== undefined && (
        <ProjectConnectorPanel
          projectId={selectedProject.id}
          projectName={selectedProject.name}
          service={service}
        />
      )}
    </>
  );
}
